import { useMemo, useState } from 'react';
import {
  CATEGORY_LABELS,
  CONDITIONS,
  IMPACT_LABELS,
  buildVariantIndex,
  matchCondition,
  searchConditions,
  type ConditionCategory,
  type ConditionMatch,
  type MarkerMatch,
} from '../lib/conditions';
import { conditionNote, copyToClipboard } from '../lib/note';
import { useVault } from '../lib/vault';
import type { ParsedGenome } from '../types';
import { Explain } from './Explain';

interface Props {
  genome: ParsedGenome;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

const STATUS_TEXT: Record<MarkerMatch['status'], string> = {
  called: 'called',
  'strand-ambiguous': 'strand unknown',
  'no-call': 'no call',
  unreadable: 'unreadable',
  mismatch: 'allele mismatch',
  absent: 'not in your file',
};

const DIRECTION_WORD: Record<string, string> = {
  risk: 'risk',
  protective: 'protective',
  trait: 'trait',
};

/** Copies of the effect allele, phrased without implying a verdict. */
function copiesText(m: MarkerMatch): string {
  const { effectAllele, direction } = m.marker;
  const word = DIRECTION_WORD[direction] ?? direction;

  // A homozygous palindromic call is the same string on either strand, so the
  // count is genuinely unknowable. Show both rather than pick one.
  if (m.status === 'strand-ambiguous') {
    return `${m.effectCopies} or ${m.alternateCopies} copies of ${effectAllele}`;
  }
  if (m.status !== 'called' || m.effectCopies === null) return '—';
  if (m.effectCopies === 0) return `no copies of ${effectAllele}`;
  if (m.effectCopies === 1) return `1 copy of ${effectAllele} (${word})`;
  return `2 copies of ${effectAllele} (${word})`;
}

function genotypeClass(m: MarkerMatch): string {
  if (m.status !== 'called' && m.status !== 'strand-ambiguous') return 'nc';
  const g = m.genotype ?? '';
  return g.length === 2 && g[0] !== g[1] ? 'het' : 'hom';
}

function MarkerRow({ m }: { m: MarkerMatch }) {
  const readable = m.status === 'called' || m.status === 'strand-ambiguous';
  // Only a definite count earns risk/protective colouring; 'trait' never does.
  const hit =
    m.status === 'called' &&
    (m.effectCopies ?? 0) > 0 &&
    m.marker.direction !== 'trait';
  const tone = m.marker.direction === 'risk' ? 'risk' : 'protective';

  return (
    <tr>
      <td className="mono">{m.marker.rsid}</td>
      <td className="mono dim">{m.marker.gene}</td>
      <td>
        {readable ? (
          <span className={`chip ${genotypeClass(m)}`}>{m.genotype}</span>
        ) : (
          <span className="chip nc">{STATUS_TEXT[m.status]}</span>
        )}
      </td>
      <td>
        <span className={hit ? `dose dose-${tone}` : 'dose'}>{copiesText(m)}</span>
        {m.status === 'strand-ambiguous' && (
          <span
            className="flag"
            title="Palindromic SNP (A/T or C/G) with a homozygous call. The two DNA strands read identically here, so which allele you carry cannot be determined from this file — both possibilities are shown."
          >
            strand unknown
          </span>
        )}
        {m.strandFlipped && (
          <span
            className="flag ok"
            title="Your file reported this SNP on the opposite strand. i-seng detected that and complemented the call to match the catalog."
          >
            flipped
          </span>
        )}
      </td>
      <td className="effect">{m.marker.effect}</td>
    </tr>
  );
}

function ConditionCard({ match }: { match: ConditionMatch }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<'ok' | 'err' | null>(null);
  const { queueNote } = useVault();
  const { condition: c } = match;

  async function copy() {
    const body = conditionNote(match, { date: today() });
    queueNote({
      kind: 'condition',
      title: c.name,
      filename: c.name.replace(/[^\w.-]+/g, '_'),
      body,
    });
    const ok = await copyToClipboard(body);
    setCopied(ok ? 'ok' : 'err');
    window.setTimeout(() => setCopied(null), 1600);
  }

  const total = c.markers.length;
  const status =
    match.covered === 0
      ? {
          // "in your file" would be a lie when a marker is present but
          // uncalled or strand-ambiguous — say what was actually resolved.
          text:
            match.present === 0
              ? `0 / ${total} markers in your file`
              : `${match.present} in your file, none resolved`,
          tone: 'none' as const,
        }
      : match.riskHits > 0
        ? {
            text: `${match.riskHits} of ${match.covered} called marker${match.covered === 1 ? '' : 's'} ${match.riskHits === 1 ? 'carries' : 'carry'} a risk allele`,
            tone: 'risk' as const,
          }
        : match.protectiveHits > 0
          ? {
              text: `${match.protectiveHits} protective allele${match.protectiveHits === 1 ? '' : 's'} present`,
              tone: 'protective' as const,
            }
          : { text: `no effect alleles in ${match.covered} called`, tone: 'clear' as const };

  return (
    <div className={`cond ${open ? 'open' : ''}`}>
      <button
        className="cond-head"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="chev" aria-hidden="true">›</span>
        <span className="cond-name">{c.name}</span>
        <span className="cat-chip">{CATEGORY_LABELS[c.category]}</span>
        {c.impact === 'clinical' && (
          <span className="cat-chip clinical">{IMPACT_LABELS[c.impact]}</span>
        )}
        <span className={`cond-status tone-${status.tone}`}>{status.text}</span>
      </button>

      {open && (
        <div className="cond-body">
          <p className="cond-summary">{c.summary}</p>

          {c.caveat && (
            <div className="note" style={{ marginBottom: 14 }}>
              <strong>Read this first.</strong> {c.caveat}
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>rsID</th>
                  <th>Gene</th>
                  <th>Your call</th>
                  <th>Effect allele</th>
                  <th>Reported association</th>
                </tr>
              </thead>
              <tbody>
                {match.markers.map((m) => (
                  <MarkerRow key={m.marker.rsid} m={m} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="cond-foot">
            <span className="cond-src">Verify against: {c.sources}</span>
            <button className="note-btn" onClick={copy} title="Copy as an Obsidian note">
              {copied === 'ok' ? '✓ copied' : copied === 'err' ? 'copy failed' : '＋ note'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function ConditionSearch({ genome }: Props) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<ConditionCategory | 'all'>('all');
  const [onlyHits, setOnlyHits] = useState(false);

  const index = useMemo(() => buildVariantIndex(genome), [genome]);

  const results = useMemo(() => {
    let list = searchConditions(query);
    if (category !== 'all') list = list.filter((c) => c.category === category);
    let matched = list.map((c) => matchCondition(c, index));
    if (onlyHits) {
      matched = matched.filter((m) => m.riskHits > 0 || m.protectiveHits > 0);
    }
    return matched;
  }, [query, category, onlyHits, index]);

  const coverage = useMemo(() => {
    const all = CONDITIONS.flatMap((c) => c.markers.map((m) => m.rsid));
    const unique = [...new Set(all)];
    const present = unique.filter((rsid) => index.has(rsid.toLowerCase())).length;
    return { present, total: unique.length };
  }, [index]);

  const categories = useMemo(
    () => [...new Set(CONDITIONS.map((c) => c.category))].sort(),
    [],
  );

  return (
    <div className="card">
      <h2>
        Conditions{' '}
        <span className="muted">
          · {results.length} of {CONDITIONS.length} · {coverage.present}/{coverage.total} catalog
          markers in your file
        </span>
        <Explain id="conditions" label="What does any of this mean?" />
      </h2>

      <div className="note" style={{ marginBottom: 16 }}>
        <strong>These are research associations, not a diagnosis.</strong> Each
        entry reports what published studies found about a genetic marker across
        large groups of people — not what will happen to you. Consumer arrays are
        not clinical-grade tests, they cover a tiny fraction of the genome, and a
        marker that is absent here says nothing about your actual risk. Nothing
        in this tab is medical advice; take real questions to a doctor or genetic
        counsellor.
      </div>

      <div className="controls">
        <input
          type="search"
          placeholder="Search a condition, gene, or rsID — e.g. diabetes, APOE, rs7903146…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search conditions"
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as ConditionCategory | 'all')}
          aria-label="Filter by category"
        >
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
          ))}
        </select>
        <label className="check">
          <input
            type="checkbox"
            checked={onlyHits}
            onChange={(e) => setOnlyHits(e.target.checked)}
          />
          Only conditions with an effect allele
        </label>
      </div>

      <div className="cond-list">
        {results.map((m) => (
          <ConditionCard key={m.condition.id} match={m} />
        ))}
        {results.length === 0 && (
          <p className="empty">
            No conditions match “{query}”. The catalog is deliberately small —
            around {CONDITIONS.length} well-replicated entries — so plenty of real
            conditions simply aren’t in it.
          </p>
        )}
      </div>
    </div>
  );
}
